"use client";
/**
 * The nutrition widget: one bounded set, in whichever layout the host selected.
 *
 * The three renderers are three real compositions. There is no
 * `renderMobileCard ?? renderRailCard` here and there cannot be: the kit
 * requires `renderCard` and uses it at BOTH widths, which is what stops a 236px
 * comparison card being served as a full-width standalone object. Each forwards
 * the set's open control, so the item's name — and nothing else in the item —
 * is the item's tab stop.
 *
 * The drill pane is the family's own detail in `Embedded` frame, so the pane
 * carries one bezel and one header rather than two of each.
 */
import type { CSSProperties, ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import {
  CresultcardSet,
  CresultcardDetailFrame,
  CresultcardLayout,
  CresultcardSetStatus,
  type CresultcardOpenControl,
  type CresultcardSetEmpty,
  type CresultcardSetFailure,
} from "@/components/ui/Cresultcard";
import { cnutritioncardKey } from "./cnutritioncard.model";
import { Cnutritioncard } from "./Cnutritioncard";
import { CnutritioncardDetail } from "./CnutritioncardDetail";
import { CnutritioncardRail } from "./CnutritioncardRail";
import { CnutritioncardRow } from "./CnutritioncardRow";
import type {
  CnutritioncardData,
  CnutritioncardServingScope,
  CnutritioncardVariant,
} from "./cnutritioncard.types";

/** Two cards side by side in the 556px column, one at Mobile's 350. */
const CARD_MIN_WIDTH = 260;
/** The drill pane arrives unpadded, so the set gives it the column's own inset. */
const DRILL_INSET = 14;
const DRILL_STYLE: CSSProperties = { minWidth: 0, padding: DRILL_INSET };

interface CnutritioncardSetProps {
  readonly items: readonly CnutritioncardData[];
  /**
   * A composition the host PINS for every card of this set, rather than the
   * tweak panel's current selection. Omitted — the dev gallery's case — the
   * family reads the registry exactly as a lone card does.
   */
  readonly variant?: CnutritioncardVariant;
  readonly servingScope?: CnutritioncardServingScope;
  readonly icon: TablerIcon;
  readonly label: string;
  readonly context?: ReactNode;
  /** What bounds the whole projection. Wraps in the set header; never clipped. */
  readonly bounds?: ReactNode;
  readonly provenance?: ReactNode;
  /** The frame the drilled pane states its own values against. */
  readonly drillContext: string;
  readonly layout: CresultcardLayout;
  /**
   * The item whose drill is open when the set mounts — an attached result's
   * `full` face (results-as-resources DECISIONS §7.2). Omitted, the set mounts
   * closed. Forwarded to the kit's set untouched.
   */
  readonly initialOpenIndex?: number;
  readonly status?: CresultcardSetStatus;
  /** Required: a set that answered with nothing still has to say what it is. */
  readonly empty: CresultcardSetEmpty;
  readonly failure?: CresultcardSetFailure;
}

export function CnutritioncardSet({
  items,
  variant,
  servingScope,
  icon,
  label,
  context,
  bounds,
  provenance,
  drillContext,
  layout,
  initialOpenIndex,
  status = CresultcardSetStatus.Ready,
  empty,
  failure,
}: CnutritioncardSetProps): ReactElement {
  const lastIndex = items.length - 1;

  return (
    <CresultcardSet<CnutritioncardData>
      bounds={bounds}
      cardMin={CARD_MIN_WIDTH}
      context={context}
      empty={empty}
      failure={failure}
      icon={icon}
      items={items}
      initialOpenIndex={initialOpenIndex}
      keyOf={cnutritioncardKey}
      label={label}
      layout={layout}
      provenance={provenance}
      renderCard={(
        item: CnutritioncardData,
        _index: number,
        control: CresultcardOpenControl | undefined,
      ): ReactNode => (
        <Cnutritioncard
          data={item}
          openControl={control}
          servingScope={servingScope}
          variant={variant}
        />
      )}
      renderExpanded={(item: CnutritioncardData): ReactNode => (
        <div
          data-el="Cnutritioncard/Set/DetailPanel"
          style={DRILL_STYLE}
        >
          <CnutritioncardDetail
            context={drillContext}
            data={item}
            frame={CresultcardDetailFrame.Embedded}
            servingScope={servingScope}
          />
        </div>
      )}
      renderRailCard={(
        item: CnutritioncardData,
        _index: number,
        control: CresultcardOpenControl | undefined,
      ): ReactNode => <CnutritioncardRail data={item} openControl={control} />}
      renderRow={(
        item: CnutritioncardData,
        index: number,
        control: CresultcardOpenControl | undefined,
      ): ReactNode => (
        <CnutritioncardRow
          data={item}
          onLast={index === lastIndex}
          openControl={control}
        />
      )}
      status={status}
      titleOf={(item: CnutritioncardData): string => item.name}
    />
  );
}
